import { serverTimestamp, Timestamp } from 'firebase/firestore';
import { isRecord, type Doc } from '@day';

/**
 * Zones (Couriers → Zones): `businesses/{id}/zones/{zoneId}`, an area the owner drew on Google's map and the couriers who
 * cover it. When stops are shared out a zone is a hard rule: a stop inside it goes only to its couriers (any of them where
 * zones overlap); a stop outside every zone can go to anyone. The corners are the owner's own clicks, so they may be kept:
 * nothing here ever makes or moves a zone from Google's positions (CLAUDE.md, Google Maps terms).
 */

import { inside, type Spot } from './geo';

export type { Spot };
export { areaKm2, inside, middleOf } from './geo';

export interface Zone {
  id: string;
  name: string;
  /** The couriers it belongs to (their uids), first one first: the zone takes the first one's colour. */
  couriers: string[];
  /** The corners, in the order they were drawn. */
  path: Spot[];
  createdAt: number | null;
}

/** The most corners a zone keeps (the rules allow 100): a district traced in a few clicks needs far fewer. */
export const MAX_CORNERS = 100;
/** The longest name the rules take. */
export const MAX_NAME = 60;

const ms = (v: unknown): number | null => (v instanceof Timestamp ? v.toMillis() : typeof v === 'number' ? v : null);

export function readZone(id: string, d: Doc): Zone | null {
  const path = Array.isArray(d.path) ? (d.path as unknown[]).filter(isRecord).flatMap((p) => (typeof p.lat === 'number' && typeof p.lng === 'number' ? [{ lat: p.lat, lng: p.lng }] : [])) : [];
  if (path.length < 3) return null;
  return {
    id,
    name: typeof d.name === 'string' && d.name.trim() ? d.name.trim() : 'A zone',
    couriers: Array.isArray(d.couriers) ? (d.couriers as unknown[]).filter((x): x is string => typeof x === 'string') : [],
    path,
    createdAt: ms(d.createdAt),
  };
}

/** A corner as kept: six decimals (about 10 cm), plenty for a district's edge. */
const round = (p: Spot): Spot => ({ lat: Math.round(p.lat * 1e6) / 1e6, lng: Math.round(p.lng * 1e6) / 1e6 });

/** What's wrong with a zone before it's written, in words, or null: the rules would refuse it. */
export function zoneProblem(z: { name: string; path: Spot[] }): string | null {
  if (!z.name.trim()) return 'it needs a name';
  if (z.name.trim().length > MAX_NAME) return `its name is longer than ${MAX_NAME} characters`;
  if (z.path.length < 3) return 'it needs at least 3 corners';
  if (z.path.length > MAX_CORNERS) return `it has more than ${MAX_CORNERS} corners`;
  return null;
}

/** A zone's record as the rules take it (firestore.rules `validZone`): `createdAt` only when it's new. */
export function zoneFields(o: { businessId: string; name: string; couriers: string[]; path: Spot[] }, isNew: boolean): Doc {
  return {
    businessId: o.businessId,
    name: o.name.trim().slice(0, MAX_NAME),
    couriers: [...new Set(o.couriers)].slice(0, 50),
    path: o.path.slice(0, MAX_CORNERS).map(round),
    ...(isNew ? { createdAt: serverTimestamp() } : {}),
    changedAt: serverTimestamp(),
  };
}

/** The zones a spot is in. */
export const zonesAt = <Z extends { path: Spot[] }>(p: Spot, zones: Z[]) => zones.filter((z) => inside(p, z.path));
